import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import LineTaxCell from '../../../common/Tax/LineTaxCell';
import { useGetShopTaxes } from '../../../services/core/settings';
import FeesBlackSvg from '../../../utility/Svg/FeesBlackSvg';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import { DOCUMENT_TAX_TYPE, LINE_TAX_KIND, isNewTaxVersion } from '../../../utility/helpers/taxCalculation';

/**
 * Fees table for the Work Order detail document.
 *
 * Copied verbatim from the invoice's InvoiceFeesTable so the work order owns its own copy and can
 * diverge without touching the invoice.
 */
const WorkOrderFeesTable = ({ invoiceData }) => {
    const { data: shopTaxesData } = useGetShopTaxes();
    const showLineTax = isNewTaxVersion(invoiceData);

    return (
        <Grid item xl={12} md={12} sm={12} xs={12} boxShadow={0} pt={'24px'}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '16px',
                    background: '#E8E8E8',
                    borderRadius: '4px 4px 0 0',
                    boxSizing: 'border-box',
                }}
            >
                <Box
                    sx={{
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',

                        '& svg': {
                            width: '24px',
                            height: '24px',
                            display: 'block',
                        },
                    }}
                >
                    <FeesBlackSvg />
                </Box>

                <Typography
                    sx={{
                        fontSize: '20px',
                        lineHeight: '32px',
                        fontWeight: 500,
                        color: '#212121',
                        letterSpacing: '0.15px',
                    }}
                >
                    Fees
                </Typography>
            </div>

            {/* Table */}
            <div className="tableScroll">
                <Table
                    aria-label="invoice fees table"
                    sx={{
                        width: '100%',
                        tableLayout: 'fixed',

                        '& .MuiTableCell-root': {
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                            color: '#212121',
                            verticalAlign: 'middle',
                        },
                    }}
                >
                    <colgroup>
                        <col style={{ width: 56 }} />
                        <col />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 100 }} />
                        {showLineTax && <col style={{ width: 52 }} />}
                    </colgroup>
                    {/* Head */}
                    <TableHead
                        sx={{
                            '& .MuiTableCell-root': {
                                fontSize: '14px',
                                lineHeight: '20px',
                                fontWeight: 500,
                                color: '#212121',
                            },
                        }}
                    >
                        <TableRow>
                            <TableCell
                                align="left"
                                sx={{
                                    width: '68px',
                                    padding: '6px 12px 6px 16px',
                                }}
                            >
                                No.
                            </TableCell>

                            <TableCell
                                sx={{
                                    padding: '6px 16px 6px 0',
                                }}
                            >
                                Description
                            </TableCell>

                            <TableCell
                                align="left"
                                sx={{
                                    width: '90px',
                                    padding: '6px 10px',
                                }}
                            >
                                Value
                            </TableCell>

                            <TableCell
                                align={showLineTax ? 'left' : 'right'}
                                sx={{
                                    width: '100px',
                                    padding: '6px 10px',
                                }}
                            >
                                Amount
                            </TableCell>

                            {showLineTax && (
                                <TableCell
                                    align="center"
                                    sx={{
                                        width: '52px',
                                        padding: '6px 16px 6px 10px',
                                    }}
                                >
                                    Tax
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>

                    {/* Body */}
                    <TableBody>
                        {Array.isArray(invoiceData?.fees) &&
                            invoiceData?.fees.map((item, index) => {
                                const calcType =
                                    item?.feesCalculationType ||
                                    (Number(item?.feesAmount) > 0 ? 'fixed_amount' : item?.feesPercentage ? 'percentage' : 'percentage');

                                const displayName = item?.feesName || item?.feesId?.name || '—';

                                const valueCell =
                                    calcType === 'fixed_amount'
                                        ? NumericEngine.format(item?.feesAmount || 0, {
                                              formatCurrency: true,
                                              trailingZeros: true,
                                          })
                                        : `${item?.feesPercentage || 0}%`;

                                return (
                                    <TableRow key={index}>
                                        {/* No */}
                                        <TableCell
                                            sx={{
                                                width: '68px',
                                                padding: '6px 12px 6px 16px',
                                                fontSize: '16px',
                                                lineHeight: '24px',
                                                fontWeight: 400,
                                                color: 'rgba(0, 0, 0, 0.87)',
                                            }}
                                        >
                                            {index + 1}
                                        </TableCell>

                                        {/* Description */}
                                        <TableCell
                                            sx={{
                                                padding: '6px 16px 6px 0',
                                                fontSize: '16px',
                                                lineHeight: '24px',
                                                fontWeight: 400,
                                                color: 'rgba(0, 0, 0, 0.87)',
                                            }}
                                        >
                                            {displayName}
                                        </TableCell>

                                        {/* Value */}
                                        <TableCell
                                            align="left"
                                            sx={{
                                                width: '90px',
                                                padding: '0 10px',
                                                fontSize: '16px',
                                                lineHeight: '24px',
                                                fontWeight: 400,
                                                color: 'rgba(0, 0, 0, 0.87)',
                                            }}
                                        >
                                            {valueCell}
                                        </TableCell>

                                        {/* Amount */}
                                        <TableCell
                                            align={showLineTax ? 'left' : 'right'}
                                            sx={{
                                                width: '100px',
                                                padding: '0 10px',
                                                fontSize: '16px',
                                                lineHeight: '24px',
                                                fontWeight: 400,
                                                color: 'rgba(0, 0, 0, 0.87)',
                                            }}
                                        >
                                            {NumericEngine.format(item?.feesAmount || 0, {
                                                formatCurrency: true,
                                                trailingZeros: true,
                                            })}
                                        </TableCell>

                                        {/* Tax */}
                                        {showLineTax && (
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    width: '52px',
                                                    padding: '6px 16px 6px 10px',
                                                }}
                                            >
                                                <LineTaxCell
                                                    taxesBreakdown={item?.taxesBreakdown}
                                                    lineAmount={item?.feesAmount || 0}
                                                    documentType={DOCUMENT_TAX_TYPE.WORK_ORDER}
                                                    shopTaxesData={shopTaxesData}
                                                    kind={LINE_TAX_KIND.FEES}
                                                    editable={false}
                                                />
                                            </TableCell>
                                        )}
                                    </TableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </div>
        </Grid>
    );
};

export default WorkOrderFeesTable;
